import {
  ArrowDownToLine, ArrowUpFromLine, BarChart3, TrendingUp, TrendingDown,
  CreditCard, MessageSquare, Landmark, Layers, Coins, Users,
  Wallet, LayoutDashboard, ArrowLeftRight, Menu, Gift, BookOpen, Rocket,
  type LucideIcon,
} from "lucide-react";

/* ── Shortcuts ── */
export interface MobileShortcut {
  key: string;
  label: string;
  subtitle: string;
  icon: LucideIcon;
  to: string;
  color: string;
  moduleKey?: string;
}

export const MOBILE_SHORTCUTS: MobileShortcut[] = [
  { key: "deposit", label: "Deposit", subtitle: "Add funds", icon: ArrowDownToLine, to: "/app/wallets", color: "text-[hsl(var(--success))]", moduleKey: "wallets" },
  { key: "withdraw", label: "Withdraw", subtitle: "Cash out", icon: ArrowUpFromLine, to: "/app/wallets", color: "text-destructive", moduleKey: "wallets" },
  { key: "exchange", label: "Exchange", subtitle: "Convert assets", icon: BarChart3, to: "/app/exchange", color: "text-primary" },
  { key: "p2p", label: "P2P", subtitle: "Peer trading", icon: MessageSquare, to: "/app/p2p", color: "text-primary", moduleKey: "p2p" },
  { key: "buy", label: "Buy", subtitle: "Instant purchase", icon: TrendingUp, to: "/app/trade/buy", color: "text-[hsl(var(--success))]" },
  { key: "sell", label: "Sell", subtitle: "Convert to cash", icon: TrendingDown, to: "/app/trade/sell", color: "text-destructive" },
  { key: "cards", label: "Cards", subtitle: "Virtual cards", icon: CreditCard, to: "/app/cards", color: "text-primary", moduleKey: "virtual_cards" },
  { key: "staking", label: "Staking", subtitle: "Earn rewards", icon: Landmark, to: "/app/staking", color: "text-primary", moduleKey: "staking" },
  { key: "futures", label: "Futures", subtitle: "Derivatives", icon: Layers, to: "/app/futures", color: "text-primary", moduleKey: "futures" },
  { key: "spot", label: "Spot", subtitle: "Order book", icon: ArrowLeftRight, to: "/app/spot", color: "text-primary", moduleKey: "spot_trading" },
  { key: "markets", label: "Markets", subtitle: "Live prices", icon: BarChart3, to: "/app/markets", color: "text-primary", moduleKey: "landing_markets" },
  { key: "copy-trading", label: "Copy", subtitle: "Copy traders", icon: Users, to: "/app/copy-trading", color: "text-primary", moduleKey: "copy_trading" },
  { key: "earn", label: "Earn", subtitle: "Yield products", icon: Coins, to: "/app/earn", color: "text-primary", moduleKey: "earn" },
  { key: "token-sale", label: "Launchpad", subtitle: "Token sales", icon: Rocket, to: "/app/token-sale", color: "text-primary", moduleKey: "token_sale" },
  { key: "notes", label: "Notes", subtitle: "Legacy notes", icon: BookOpen, to: "/app/notes-market", color: "text-primary", moduleKey: "legacy_notes" },
  { key: "referrals", label: "Referrals", subtitle: "Invite & earn", icon: Gift, to: "/app/referrals", color: "text-primary", moduleKey: "referrals" },
  { key: "wallets", label: "Wallets", subtitle: "All balances", icon: Wallet, to: "/app/wallets", color: "text-primary", moduleKey: "wallets" },
];

/** Default shortcut order — Buy and P2P swapped vs. the old hardcoded layout. */
export const DEFAULT_SHORTCUTS = [
  "deposit", "withdraw", "exchange", "p2p",
  "buy", "sell", "cards",
  "staking", "futures",
];

/** The first N shortcuts render in the compact top action row. */
export const PRIMARY_SHORTCUT_COUNT = 4;

/* ── Sections ── */
export interface MobileSection {
  key: string;
  label: string;
  pinned?: boolean;
  moduleKey?: string;
}

export const MOBILE_SECTIONS: MobileSection[] = [
  { key: "chart", label: "Portfolio Chart" },
  { key: "shortcuts", label: "Shortcuts" },
  { key: "wallets", label: "My Wallets" },
  { key: "kyc", label: "Verification" },
  { key: "referral", label: "Referral Card", moduleKey: "referrals" },
  { key: "support", label: "Quick Support" },
  { key: "community", label: "Community" },
  { key: "tokensale", label: "Launchpad", moduleKey: "token_sale" },
  { key: "activity", label: "Recent Activity" },
];

export const DEFAULT_SECTIONS = MOBILE_SECTIONS.map((s) => s.key);

/* ── Bottom tabs ── */
export interface MobileTab {
  key: string;
  to: string;
  label: string;
  icon: LucideIcon;
  moduleKey?: string;
  fixed?: boolean;
}

export const MOBILE_TABS: MobileTab[] = [
  { key: "home", to: "/app/dashboard", label: "Home", icon: LayoutDashboard },
  { key: "markets", to: "/app/markets", label: "Markets", icon: BarChart3, moduleKey: "landing_markets" },
  { key: "trade", to: "/app/spot", label: "Trade", icon: ArrowLeftRight, moduleKey: "spot_trading" },
  { key: "wallet", to: "/app/wallets", label: "Wallet", icon: Wallet, moduleKey: "wallets" },
  { key: "p2p", to: "/app/p2p", label: "P2P", icon: MessageSquare, moduleKey: "p2p" },
  { key: "futures", to: "/app/futures", label: "Futures", icon: Layers, moduleKey: "futures" },
  { key: "staking", to: "/app/staking", label: "Staking", icon: Landmark, moduleKey: "staking" },
  { key: "cards", to: "/app/cards", label: "Cards", icon: CreditCard, moduleKey: "virtual_cards" },
  { key: "more", to: "/app/more", label: "More", icon: Menu, fixed: true },
];

/** 4 configurable tabs; "More" is always pinned last. */
export const DEFAULT_TABS = ["home", "markets", "trade", "wallet"];
export const MAX_CONFIGURABLE_TABS = 4;

export const shortcutByKey = (k: string) => MOBILE_SHORTCUTS.find((s) => s.key === k);
export const sectionByKey = (k: string) => MOBILE_SECTIONS.find((s) => s.key === k);
export const tabByKey = (k: string) => MOBILE_TABS.find((t) => t.key === k);
